import { useMutation } from '@apollo/client';
import {
  DashboardColumnType,
  IDashboardInstanceFieldsFragment,
  IDashboardRichTextEditMutation,
  IDashboardRichTextEditMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { DASHBOARD_RICH_TEXT_EDIT } from '../../../../utils/apollo/dashboards';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { assertNotNil, GraphQLID } from '../../../../utils/types';
import { useCallback } from 'react';
import useDashboardUpdateGrid from '../grid/useDashboardUpdateGrid';
import { getRowsWithUpdatedContent } from '../grid/gridUpdaters';
import useDashboardReload from '../useDashboardReload';

interface IDashboardEditRichTextProps {
  richTextId: GraphQLID;
  html: string;
}

const useDashboardEditRichText = (
  dashboard: IDashboardInstanceFieldsFragment,
) => {
  const reloadDashboard = useDashboardReload(dashboard.baseId);
  const { updateDashboardGrid } = useDashboardUpdateGrid(dashboard);

  const [editRichTextMutation] = useMutation<
    IDashboardRichTextEditMutation,
    IDashboardRichTextEditMutationVariables
  >(DASHBOARD_RICH_TEXT_EDIT);

  const editDashboardRichText = useCallback(
    async ({ richTextId, html }: IDashboardEditRichTextProps) => {
      const { data } = await editRichTextMutation({
        variables: {
          input: {
            baseId: dashboard.baseId,
            instanceId: dashboard.id,
            richTextId,
            html,
          },
        },
      });

      const result = data?.dashboardRichTextEdit;
      if (printGraphqlErrors(result?.errors)) {
        await reloadDashboard();
        return;
      }

      const newRichText = result?.richText;
      assertNotNil(newRichText);

      updateDashboardGrid(
        getRowsWithUpdatedContent({
          type: DashboardColumnType.RichText,
          richText: newRichText,
        }),
      );
    },
    [
      dashboard.baseId,
      dashboard.id,
      editRichTextMutation,
      reloadDashboard,
      updateDashboardGrid,
    ],
  );

  return {
    editDashboardRichText,
  };
};

export default useDashboardEditRichText;
